import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sparkles, User, LogOut, Settings, CreditCard, History, LayoutDashboard } from 'lucide-react';

export function Navbar({ variant = 'default' }) {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isApp = location.pathname.startsWith('/app');
  const bgClass = variant === 'transparent' ? 'bg-transparent' : 'glass-header';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className={`${bgClass} sticky top-0 z-50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <Link to={isAuthenticated ? '/app' : '/'} className="flex items-center gap-2">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">JobMatch</span>
          </Link>

          <nav className="flex items-center gap-4">
            {!isAuthenticated ? (
              <>
                <Link to="/pricing">
                  <Button variant="ghost" className="text-slate-600 hover:text-slate-900">
                    Pricing
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600 hover:text-slate-900">
                    Log in
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="bg-slate-900 text-white hover:bg-slate-800">
                    Get Started
                  </Button>
                </Link>
              </>
            ) : (
              <>
                {!isApp && (
                  <Link to="/app">
                    <Button variant="ghost" className="text-slate-600 hover:text-slate-900">
                      Dashboard
                    </Button>
                  </Link>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span className="hidden sm:inline">{user?.name || 'Account'}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                      <p className="text-xs text-lime-600 font-medium mt-1 capitalize">
                        {user?.plan || 'Free'} Plan
                      </p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/app')} className="cursor-pointer">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/app/history')} className="cursor-pointer">
                      <History className="w-4 h-4 mr-2" />
                      History
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/app/billing')} className="cursor-pointer">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Billing
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/app/settings')} className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
